import { createAction, createSlice } from '@reduxjs/toolkit';
import { get } from 'lodash';
import { geneAsyncAction } from 'services/actionConfigs';
import { RootState } from 'store';
import { useAppSelector } from 'store/hooks';
import { ICustomerType, ICustomerListResponse, featureName, UpdateCustomerBody, GetCustomerListParams, ICustomerSummary, ICustomerTicketListResponse, GetTicketByCustomerIdParams } from './constants';

const createAsync = geneAsyncAction(featureName);

const async = {
  getCustomerList: createAsync<GetCustomerListParams, ICustomerListResponse>('getCustomerList'),
  getCustomerDetail: createAsync<string, ICustomerType>('getCustomerDetail'),
  getCustomerSummary: createAsync<void, ICustomerSummary>('getCustomerSummary'),
  getTicketsByCustomerId: createAsync<GetTicketByCustomerIdParams, ICustomerTicketListResponse>('getTicketsByCustomerId'),
};

const editCustomer = createAction<UpdateCustomerBody>('editCustomer');
const setCustomerData = createAction<{
  sortBy: 'ASC' | 'DESC' | '';
  sortKey: 'point' | 'amount' | '';
}>('setCustomerData');
const updateCustomerPoint = createAction<{ id: string; point: number }>('updateCustomerPoint');

export type ICustomerReducerState = {
  isLoading: boolean;
  customerData: ICustomerListResponse | null;
  selectedCustomer: ICustomerType | null;
  customerSorts: {
    sortBy: 'ASC' | 'DESC' | '';
    sortKey: 'point' | 'amount' | '';
  };
  customerSummary: ICustomerSummary | null;
  ticketsByCustomerId: ICustomerTicketListResponse | null;
};

type MyState = ICustomerReducerState;

const getCurrentState = (state: RootState): MyState => state[featureName];

const selector = <T = MyState>(key: keyof T, defaultValue?: any) => useAppSelector((state) => get(getCurrentState(state), key, defaultValue));

const selectors = {
  isLoading: () => selector('isLoading') as MyState['isLoading'],
  customerData: () => selector('customerData') as MyState['customerData'],
  selectedCustomer: () => selector('selectedCustomer') as MyState['selectedCustomer'],
  customerSorts: () => selector('customerSorts') as MyState['customerSorts'],
  customerSummary: () => selector('customerSummary') as MyState['customerSummary'],
  ticketsByCustomerId: () => selector('ticketsByCustomerId') as MyState['ticketsByCustomerId'],
};

const Slice = createSlice({
  name: featureName,
  initialState: {
    isLoading: false,
    customerData: null,
    selectedCustomer: null,
    customerSorts: {
      sortBy: '',
      sortKey: '',
    },
    ticketsByCustomerId: null,
  } as MyState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(async.getCustomerList.fetch, (state) => {
        state.isLoading = true;
      })
      .addCase(async.getCustomerList.success, (state, { payload }) => {
        state.customerData = payload;
        state.isLoading = false;
      })
      .addCase(async.getCustomerList.fail, (state) => {
        state.isLoading = false;
      })
      .addCase(async.getCustomerDetail.fetch, (state) => {
        state.isLoading = true;
      })
      .addCase(async.getCustomerDetail.success, (state, { payload }) => {
        state.selectedCustomer = payload;
        state.isLoading = false;
      })
      .addCase(async.getCustomerDetail.fail, (state) => {
        state.isLoading = false;
      })
      .addCase(editCustomer, (state, { payload }) => {
        if (state.customerData) {
          state.customerData = {
            ...state.customerData,
            content: state.customerData.content.map((customer) => (customer.id === payload.id ? { ...customer, ...payload } : customer)),
          };
        }
      })
      .addCase(setCustomerData, (state, { payload }) => {
          state.customerSorts = payload;
      })
      .addCase(updateCustomerPoint, (state, { payload }) => {
        if (state.customerData) {
          state.customerData.content = state.customerData.content.map((customer) => (customer.id === payload.id ? { ...customer, point: payload.point } : customer));
        }
      })
      .addCase(async.getTicketsByCustomerId.fetch, (state) => {
        state.isLoading = true;
      })
      .addCase(async.getTicketsByCustomerId.success, (state, { payload }) => {
        state.ticketsByCustomerId = payload;
        state.isLoading = false;
      })
      .addCase(async.getTicketsByCustomerId.fail, (state) => {
        state.isLoading = false;
      })
      .addCase(async.getCustomerSummary.success, (state, { payload }) => {
        state.customerSummary = payload;
        state.isLoading = false;
      });
  },
});

const actions = {
  ...Slice.actions,
  async,
  editCustomer,
  setCustomerData,
  updateCustomerPoint,
};

const reducer = Slice.reducer;

export { actions, reducer, selectors };
